import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Button } from './ui'
import { api, ApiError } from '../api/client'
import { humanBytes } from '../lib/format'
import { EnrollTokenReveal } from './EnrollTokenReveal'

/** Shown when step 1 finds no published installer artifact at all. */
export const NO_ARTIFACT_MESSAGE =
  'No installer available — an installer ISO or Proxmox image must be published before gateways can be enrolled (see the publish workflow).'

type Artifact = 'iso' | 'qcow2'

interface ArtifactSpec {
  title: string
  blurb: string
  filename: string
  probe: () => Promise<void>
  download: (onProgress?: (bytes: number) => void) => Promise<Blob>
}

const ARTIFACTS: Record<Artifact, ArtifactSpec> = {
  iso: {
    title: 'Installer ISO',
    blurb: 'Bare metal / any hypervisor — flash to USB and boot.',
    filename: 'ostrel-edge-installer.iso',
    probe: api.probeEdgeIso,
    download: api.downloadEdgeIso,
  },
  qcow2: {
    title: 'Proxmox image',
    blurb: 'qcow2 — import and boot on Proxmox VE.',
    filename: 'ostrel-edge.qcow2',
    probe: api.probeEdgeImage,
    download: api.downloadEdgeImage,
  },
}

type Availability = 'probing' | 'available' | 'unpublished' | 'error'

type Download =
  | { phase: 'starting' }
  | { phase: 'downloading'; bytes: number }
  | { phase: 'downloaded'; bytes: number }
  | { phase: 'failed'; notPublished: boolean; detail: string }

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

/** Install docs for the ISO path: flash, boot, enroll. */
function IsoDocs() {
  return (
    <ol className="flex list-decimal flex-col gap-1 pl-4 text-xs leading-relaxed text-ostrel-ink-muted">
      <li>
        Flash <span className="font-mono">ostrel-edge-installer.iso</span> to a USB stick
        (balenaEtcher, dd, …) or attach it to a virtual machine.
      </li>
      <li>
        Boot the machine from it on the customer's network — the installer provisions the
        appliance and reboots.
      </li>
      <li>
        On first boot the setup console asks for the one-time enroll token — the token in step 3.
      </li>
    </ol>
  )
}

const QCOW2_COMMANDS = `qm create 900 --name ostrel-edge --memory 1024 --cores 1 \\
    --net0 virtio,bridge=vmbr0 --machine q35 --bios ovmf --ostype l26 \\
    --serial0 socket
qm set 900 --efidisk0 <storage>:1,efitype=4m,pre-enrolled-keys=0
qm importdisk 900 ostrel-edge.qcow2 <storage>
# qm importdisk prints the created volume (e.g. local-lvm:vm-900-disk-1) —
# attach exactly that:
qm set 900 --scsihw virtio-scsi-single --scsi0 <storage>:vm-900-disk-1,discard=on,ssd=1
qm set 900 --boot order=scsi0`

/** Install docs for the qcow2 path (condensed from ostrel-edge docs/publish.md). */
function Qcow2Docs() {
  return (
    <div className="flex flex-col gap-2 text-xs leading-relaxed text-ostrel-ink-muted">
      <p>
        Copy <span className="font-mono">ostrel-edge.qcow2</span> to the Proxmox host (scp), then
        create and import the VM:
      </p>
      <pre className="overflow-x-auto rounded-sm border border-ostrel-line bg-ostrel-sand px-3 py-2 font-mono text-[11px] leading-relaxed text-ostrel-ink">
        {QCOW2_COMMANDS}
      </pre>
      <ol className="flex list-decimal flex-col gap-1 pl-4">
        <li>
          UEFI (OVMF) is required — the image's only bootloader lives on the ESP. Secure boot must
          stay off (<span className="font-mono">pre-enrolled-keys=0</span>): the bootloader is
          unsigned.
        </li>
        <li>
          Serial console: set the VM's Display to "Serial terminal 0" (or{' '}
          <span className="font-mono">qm terminal 900</span>). The first-boot setup prompts run on
          the graphical console (tty1, the noVNC console), exactly like ISO installs.
        </li>
        <li>
          Boot the VM — the first-boot setup asks for the one-time enroll token (step 3). The root
          filesystem grows to the disk size automatically.
        </li>
      </ol>
    </div>
  )
}

/**
 * Three-step gateway enrollment wizard, shared by gateway create (Gateways)
 * and reinstall (GatewayDetail):
 *  1. Choose the installer artifact. Both /edge/iso and /edge/image are
 *     probed up front; an unpublished artifact's card is disabled, and when
 *     NEITHER exists the flow aborts here — `enroll` is never called (no
 *     mutation before an artifact exists).
 *  2. Selecting a card mints the token (`enroll`) AND starts the artifact
 *     download; the matching install docs render inline.
 *  3. The enroll token stays hidden behind an explicit "I'm ready to input"
 *     gate, enabled only once the download completed.
 */
export function EnrollWizard({
  enroll,
  onDone,
  intro,
}: {
  /** Mints the enroll token (create / re-enroll) and returns it plaintext. */
  enroll: () => Promise<string>
  onDone: () => void
  /** Optional context shown above the step-1 cards (e.g. the reinstall warning). */
  intro?: ReactNode
}) {
  const [availability, setAvailability] = useState<Record<Artifact, Availability>>({
    iso: 'probing',
    qcow2: 'probing',
  })
  const [artifact, setArtifact] = useState<Artifact | null>(null)
  const [download, setDownload] = useState<Download>({ phase: 'starting' })
  const [token, setToken] = useState<string | null>(null)
  const [enrollError, setEnrollError] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  /** StrictMode runs the mount effect twice in dev — probe once. */
  const probedRef = useRef(false)

  useEffect(() => {
    if (probedRef.current) return
    probedRef.current = true
    for (const key of Object.keys(ARTIFACTS) as Artifact[]) {
      ARTIFACTS[key].probe().then(
        () => setAvailability((a) => ({ ...a, [key]: 'available' })),
        (e: unknown) =>
          setAvailability((a) => ({
            ...a,
            [key]: e instanceof ApiError && e.status === 404 ? 'unpublished' : 'error',
          })),
      )
    }
  }, [])

  const startDownload = useCallback(async (key: Artifact) => {
    setDownload({ phase: 'starting' })
    try {
      const blob = await ARTIFACTS[key].download((bytes) =>
        setDownload({ phase: 'downloading', bytes }),
      )
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = ARTIFACTS[key].filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Revoke lazily: revoking right after the click can abort the save.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
      setDownload({ phase: 'downloaded', bytes: blob.size })
    } catch (e) {
      setDownload({
        phase: 'failed',
        notPublished: e instanceof ApiError && e.status === 404,
        detail: errDetail(e),
      })
    }
  }, [])

  const mint = useCallback(async () => {
    setEnrollError(null)
    try {
      setToken(await enroll())
    } catch (e) {
      setEnrollError(errDetail(e))
    }
  }, [enroll])

  const select = (key: Artifact) => {
    if (artifact !== null || availability[key] !== 'available') return
    setArtifact(key)
    void startDownload(key)
    void mint()
  }

  const probingDone = availability.iso !== 'probing' && availability.qcow2 !== 'probing'
  const noneAvailable = probingDone && availability.iso !== 'available' && availability.qcow2 !== 'available'
  const ready = download.phase === 'downloaded' && token !== null

  if (confirmed && token !== null) {
    return <EnrollTokenReveal token={token} onDone={onDone} />
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
          Step 1 — What do you want to install?
        </p>
        {intro}
        {noneAvailable && (
          <p className="mb-2 rounded-sm border border-ostrel-warn/50 bg-ostrel-warn/10 px-3 py-2 text-xs text-ostrel-warn">
            {NO_ARTIFACT_MESSAGE}
          </p>
        )}
        <div className="grid gap-2 sm:grid-cols-2">
          {(Object.keys(ARTIFACTS) as Artifact[]).map((key) => {
            const spec = ARTIFACTS[key]
            const state = availability[key]
            const disabled = state !== 'available' || artifact !== null
            const selected = artifact === key
            return (
              <button
                key={key}
                type="button"
                disabled={disabled}
                onClick={() => select(key)}
                className={`rounded-sm border px-3 py-2 text-left ${
                  selected
                    ? 'border-ostrel-gold bg-ostrel-gold/10'
                    : disabled
                      ? 'cursor-not-allowed border-ostrel-line opacity-60'
                      : 'border-ostrel-line hover:border-ostrel-gold'
                }`}
              >
                <span className="block text-sm font-medium text-ostrel-ink">{spec.title}</span>
                <span className="mt-0.5 block text-xs text-ostrel-ink-muted">{spec.blurb}</span>
                {state === 'probing' && (
                  <span className="mt-1 block text-[11px] text-ostrel-ink-muted">Checking…</span>
                )}
                {state === 'unpublished' && (
                  <span className="mt-1 block text-[11px] text-ostrel-warn">Not published yet</span>
                )}
                {state === 'error' && (
                  <span className="mt-1 block text-[11px] text-ostrel-danger">
                    Could not verify availability
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {artifact !== null && (
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Step 2 — Download + install
          </p>
          {download.phase === 'starting' && (
            <p className="text-xs text-ostrel-ink-muted">Starting the download…</p>
          )}
          {download.phase === 'downloading' && (
            <p className="text-xs text-ostrel-ink-muted">
              Downloading {ARTIFACTS[artifact].filename}…{' '}
              <span className="tnum">{humanBytes(download.bytes)}</span>
            </p>
          )}
          {download.phase === 'downloaded' && (
            <p className="rounded-sm border border-ostrel-ok/40 bg-ostrel-ok/10 px-3 py-2 text-xs text-ostrel-ok">
              Downloaded {ARTIFACTS[artifact].filename} (
              <span className="tnum">{humanBytes(download.bytes)}</span>).
            </p>
          )}
          {download.phase === 'failed' && download.notPublished && (
            <p className="rounded-sm border border-ostrel-warn/50 bg-ostrel-warn/10 px-3 py-2 text-xs text-ostrel-warn">
              The {ARTIFACTS[artifact].title} is not published yet — ask your admin to publish one
              (publish workflow).
            </p>
          )}
          {download.phase === 'failed' && !download.notPublished && (
            <div className="flex flex-col gap-2">
              <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
                The download failed: {download.detail}
              </p>
              <div>
                <Button variant="navy" size="sm" onClick={() => void startDownload(artifact)}>
                  Retry download
                </Button>
              </div>
            </div>
          )}
          {(download.phase === 'downloaded' || download.phase === 'failed') && (
            <button
              type="button"
              className="mt-1.5 text-xs text-ostrel-gold hover:underline"
              onClick={() => void startDownload(artifact)}
            >
              Download again
            </button>
          )}
          <div className="mt-2">{artifact === 'iso' ? <IsoDocs /> : <Qcow2Docs />}</div>
          {enrollError && (
            <div className="mt-2 flex flex-col gap-2">
              <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
                Could not mint the enroll token: {enrollError}
              </p>
              <div>
                <Button variant="navy" size="sm" onClick={() => void mint()}>
                  Retry
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {artifact !== null && (
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Step 3 — Enroll token
          </p>
          <div className="flex flex-col gap-3">
            <p className="rounded-sm border border-ostrel-line bg-ostrel-sand px-3 py-2 text-xs text-ostrel-ink-muted">
              The enroll token unlocks after the download completes.
            </p>
            <div>
              <Button variant="navy" size="sm" disabled={!ready} onClick={() => setConfirmed(true)}>
                I'm ready to input
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
